/**
 * VENDORED from `@crehana/compass.web/src/page-layout/dashboard.tsx`
 * (CORE-1671). Adapted for this app: this app has no `ProductInfoProvider`
 * / white-labeled sidebar data source (`@crehana/layouts` is pinned at
 * 77.13.54, which predates that API), so there is no sidebar-based
 * breadcrumb/title inference here. `title` and `breadcrumbItems` are always
 * explicit props — no `useDashboardBreadcrumb` hook, no sidebar lookup.
 */
import type { ReactNode } from 'react';

import { Tab, Tabs } from '@crehana/compass.ui';

import {
  PageLayout,
  PageLayoutBreadcrumb,
  PageLayoutContent,
  PageLayoutSubtitle,
  PageLayoutTitle,
  PageLayoutTop,
  type TPageLayoutBreadcrumbItem,
} from './page-layout';

export type TDashboardPageProps = {
  /** No sidebar inference in this app — pass explicitly. */
  title?: string;
  description: string;
  /** Override the visual subtitle with rich content. Falls back to `description`. */
  subtitle?: ReactNode;
  children: ReactNode;
  /** Renders a tab strip flush against the content area below the title row. */
  tabs?: ReactNode;
  /** Action buttons rendered to the right of the title row. */
  cta?: ReactNode;
  /** Explicit crumbs; `false` opts out entirely (no crumbs rendered). */
  breadcrumbItems?: TPageLayoutBreadcrumbItem[] | false;
  /** Leaf crumbs appended after `breadcrumbItems`. */
  extendBreadcrumb?: TPageLayoutBreadcrumbItem[];
  /** Root crumbs prepended before `breadcrumbItems` — e.g. a "Inicio" home crumb. */
  prependBreadcrumb?: TPageLayoutBreadcrumbItem[];
};

// No sidebar inference in this app — breadcrumb/title always come from
// explicit props. `breadcrumbItems: false` opts out entirely; otherwise
// `prependBreadcrumb` + `breadcrumbItems` + `extendBreadcrumb` are merged
// in order.
const resolveExplicit = ({
  title,
  breadcrumbItems,
  prependBreadcrumb,
  extendBreadcrumb,
}: {
  title?: string;
  breadcrumbItems?: TPageLayoutBreadcrumbItem[] | false;
  prependBreadcrumb?: TPageLayoutBreadcrumbItem[];
  extendBreadcrumb?: TPageLayoutBreadcrumbItem[];
}) => {
  if (breadcrumbItems === false) {
    return {
      title: title ?? '',
      breadcrumbItems: undefined,
      isLoading: false,
    };
  }

  const merged = [
    ...(prependBreadcrumb || []),
    ...(breadcrumbItems || []),
    ...(extendBreadcrumb || []),
  ];

  return {
    title: title ?? '',
    breadcrumbItems: merged.length ? merged : undefined,
    isLoading: false,
  };
};

const DashboardPageLayoutBase = ({
  title,
  description,
  subtitle,
  children,
  tabs,
  cta,
  breadcrumbItems,
  extendBreadcrumb,
  prependBreadcrumb,
}: TDashboardPageProps) => {
  const {
    title: resolvedTitle,
    breadcrumbItems: resolvedBreadcrumbItems,
    isLoading,
  } = resolveExplicit({
    title,
    breadcrumbItems,
    extendBreadcrumb,
    prependBreadcrumb,
  });

  const shouldFlushHeader = !!tabs;

  return (
    <PageLayout
      metadata={{
        title: resolvedTitle,
        description,
      }}
    >
      <PageLayoutTop className={shouldFlushHeader ? 'tw-pb-0' : 'tw-pb-2'}>
        {resolvedBreadcrumbItems || isLoading ? (
          <PageLayoutBreadcrumb
            items={resolvedBreadcrumbItems}
            isLoading={isLoading}
          />
        ) : null}

        <div className="tw-flex tw-items-end tw-justify-between tw-gap-3.2">
          <div className="tw-space-y-0.8">
            {/* `intercomTarget` dropped: installed compass.ui HeaderTitle
                (58.26.1) does not accept it (upstream is newer). */}
            <PageLayoutTitle title={resolvedTitle} />
            <PageLayoutSubtitle>{subtitle ?? description}</PageLayoutSubtitle>
          </div>
          {cta && <div className="tw-shrink-0">{cta}</div>}
        </div>

        {tabs}
      </PageLayoutTop>
      <PageLayoutContent>{children}</PageLayoutContent>
    </PageLayout>
  );
};

export const DashboardPageLayout = Object.assign(DashboardPageLayoutBase, {
  Tabs,
  Tab,
});
